/**
 * RAG (Retrieval-Augmented Generation) Management Page
 * Manage documents and files for RAG system
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Menu,
  MenuItem,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  InsertDriveFile as InsertDriveFileIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
} from '@mui/icons-material';
import { toast } from 'sonner';
import api from '../../interceptors/interceptor';
import { API_ROUTES } from '../../constants/apiRoutes';
import { ROUTE_PATHS } from '../../constants/routePaths';

interface FileItem {
  filename: string;
  size: number;
  created_at: string;
  modified_at: string;
  path: string;
  id: string;
  original_filename: string;
  ingested: boolean;
  created_by: string | null;
  updated_by: string | null;
  db_created_at: string;
  db_updated_at: string;
}

interface FileListResponse {
  status: string;
  total_files: number;
  files: FileItem[];
}

interface IngestionProgress {
  type: 'started' | 'progress' | 'completed' | 'error';
  message: string;
  total_files?: number;
  job_id?: string;
  data?: {
    total_chunks?: number;
    total_images?: number;
    total_edges?: number;
    files_succeeded?: number;
    files_failed?: number;
  };
}

interface JobHistoryItem {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  filenames: string[];
  embedding_mode: string;
  total_files: number;
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  files_succeeded: number;
  files_failed: number;
}

interface JobHistoryResponse {
  total: number;
  jobs: JobHistoryItem[];
}

export function RagPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [isIngesting, setIsIngesting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [ingestDialogOpen, setIngestDialogOpen] = useState(false);
  
  // Ingestion status tracking for table
  const [ingestionStatus, setIngestionStatus] = useState<{
    isIngesting: boolean;
    message: string;
    progress?: IngestionProgress;
  }>({ isIngesting: false, message: '' });
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasStartedSSE = useRef(false); // Prevent double SSE connections

  // Fetch files on component mount
  useEffect(() => {
    fetchFiles();
  }, []);

  // Handle SSE connection from navigation state or check for processing jobs
  useEffect(() => {
    // Check if we have a job_id from navigation state (priority)
    const state = location.state as { jobId?: string };
    if (state?.jobId && !hasStartedSSE.current) {
      console.log('🔄 Job ID received from navigation:', state.jobId);
      hasStartedSSE.current = true;
      
      setIngestionStatus({
        isIngesting: true,
        message: 'Processing documents...',
      });
      openSSEConnection(state.jobId);
      
      // Clear the state to prevent restarting on refresh
      window.history.replaceState({}, document.title);
    } else if (!hasStartedSSE.current) {
      // If no job_id from navigation, check for processing jobs from history
      console.log('📋 No job_id from navigation, checking history for processing jobs...');
      checkProcessingJobs();
    }
  }, [location.state]);

  // Cleanup SSE connection on unmount
  useEffect(() => {
    return () => {
      closeSSEConnection();
      hasStartedSSE.current = false;
    };
  }, []);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const response = await api.get<FileListResponse>(API_ROUTES.RAG_LIST_FILES, {
        transformRequest: [(data, headers) => {
          headers['Authorization'] = `Bearer ${window.env.REACT_APP_LLM_API_KEY}`;
          return data;
        }, ...((api.defaults.transformRequest as any) || [])],
      });
      
      if (response.data && response.data.files) {
        setFiles(response.data.files);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to load files';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const checkProcessingJobs = async () => {
    try {
      console.log('🔍 Checking for processing jobs...');
      const response = await api.get<JobHistoryResponse>(API_ROUTES.RAG_INGEST_HISTORY, {
        transformRequest: [(data, headers) => {
          headers['Authorization'] = `Bearer ${window.env.REACT_APP_LLM_API_KEY}`;
          return data;
        }, ...((api.defaults.transformRequest as any) || [])],
      });

      if (response.data && response.data.jobs) {
        // Find the first job with "processing" status
        const processingJob = response.data.jobs.find(job => job.status === 'processing');
        
        if (processingJob) {
          console.log('🔄 Found processing job:', processingJob.job_id);
          hasStartedSSE.current = true;
          
          setIngestionStatus({
            isIngesting: true,
            message: 'Resuming ingestion...',
          });
          
          openSSEConnection(processingJob.job_id);
        } else {
          console.log('✅ No processing jobs found');
        }
      }
    } catch (error: any) {
      console.error('❌ Failed to check processing jobs:', error);
      // Don't show error toast - this is a background check
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, file: FileItem) => {
    setAnchorEl(event.currentTarget);
    setSelectedFile(file);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedFile(null);
  };

  const handleDownload = async () => {
    if (!selectedFile) return;

    try {
      toast.info('Downloading file...');
      
      // Remove leading slash from path to avoid double slash in URL
      const filePath = selectedFile.path.startsWith('/') ? selectedFile.path.substring(1) : selectedFile.path;
      
      const response = await api.get(API_ROUTES.RAG_DOWNLOAD_FILE(filePath), {
        responseType: 'blob',
        transformRequest: [(data, headers) => {
          headers['Authorization'] = `Bearer ${window.env.REACT_APP_LLM_API_KEY}`;
          return data;
        }, ...((api.defaults.transformRequest as any) || [])],
      });

      // Create a download link and trigger it
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', selectedFile.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('File downloaded successfully');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to download file';
      toast.error(errorMessage);
    } finally {
      handleMenuClose();
    }
  };

  const handleDeleteClick = () => {
    setAnchorEl(null); // Close menu without clearing selectedFile
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedFile) return;

    setIsDeleting(true);
    try {
      toast.info('Deleting file...');
      
      await api.delete(API_ROUTES.RAG_DELETE_FILES, {
        data: {
          filenames: [selectedFile.filename]
        },
        transformRequest: [(data, headers) => {
          headers['Authorization'] = `Bearer ${window.env.REACT_APP_LLM_API_KEY}`;
          return data;
        }, ...((api.defaults.transformRequest as any) || [])],
      });

      toast.success('File deleted successfully');
      
      // Refresh the file list
      await fetchFiles();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete file';
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setSelectedFile(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedFile(null);
  };

  const handleBulkDeleteClick = () => {
    setBulkDeleteDialogOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedFiles.length === 0) return;

    setIsDeleting(true);
    try {
      toast.info(`Deleting ${selectedFiles.length} file(s)...`);
      
      await api.delete(API_ROUTES.RAG_DELETE_FILES, {
        data: {
          filenames: selectedFiles
        },
        transformRequest: [(data, headers) => {
          headers['Authorization'] = `Bearer ${window.env.REACT_APP_LLM_API_KEY}`;
          return data;
        }, ...((api.defaults.transformRequest as any) || [])],
      });

      toast.success(`${selectedFiles.length} file(s) deleted successfully`);
      setSelectedFiles([]);
      
      // Refresh the file list
      await fetchFiles();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete files';
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
      setBulkDeleteDialogOpen(false);
    }
  };

  const handleBulkDeleteCancel = () => {
    setBulkDeleteDialogOpen(false);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allFilenames = files.map((file) => file.filename);
      setSelectedFiles(allFilenames);
    } else {
      setSelectedFiles([]);
    }
  };

  const handleSelectFile = (filename: string) => {
    setSelectedFiles((prev) => {
      if (prev.includes(filename)) {
        return prev.filter((f) => f !== filename);
      } else {
        return [...prev, filename];
      }
    });
  };

  const handleIngestClick = () => {
    setIngestDialogOpen(true);
  };

  const handleIngestConfirm = async () => {
    if (selectedFiles.length === 0) return;

    setIsIngesting(true);
    try {
      toast.info(`Ingesting ${selectedFiles.length} file(s)...`);
      
      const response = await api.post(
        API_ROUTES.RAG_INGEST_FROM_PVC,
        { filenames: selectedFiles, "embedding_mode": "gemma" },
        {
          transformRequest: [(data, headers) => {
            headers['Authorization'] = `Bearer ${window.env.REACT_APP_LLM_API_KEY}`;
            return data;
          }, ...((api.defaults.transformRequest as any) || [])],
        }
      );

      toast.success(response.data.message || 'Files ingested successfully');
      setSelectedFiles([]);
      fetchFiles();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to ingest files';
      toast.error(errorMessage);
    } finally {
      setIsIngesting(false);
      setIngestDialogOpen(false);
    }
  };

  const handleIngestCancel = () => {
    setIngestDialogOpen(false);
  };

  // Open SSE connection to track ingestion progress using fetch API
  const openSSEConnection = async (jobId: string) => {
    try {
      const baseURL = (api.defaults.baseURL || window.env.REACT_APP_LLM_BASE_URL || '').replace(/\/$/, '');
      const eventPath = API_ROUTES.RAG_INGEST_EVENTS(jobId);
      const url = `${baseURL}${eventPath}`;
      
      console.log('🔗 Connecting to SSE:', url);
      
      // Get the authorization token
      const token = localStorage.getItem('llm-api-token') || window.env.REACT_APP_LLM_API_KEY || '';
      
      // Create abort controller for cleanup
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Use fetch API with streaming for better header control
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/event-stream',
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache',
        },
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`SSE connection failed: ${response.statusText}`);
      }

      console.log('✅ SSE connection opened');

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Response body is not readable');
      }

      // Read the stream
      let buffer = '';
      
      const readStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              console.log('📡 SSE stream ended');
              break;
            }

            // Decode the chunk and add to buffer
            buffer += decoder.decode(value, { stream: true });
            
            // Process complete messages (split by double newline)
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep incomplete line in buffer

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const jsonData = line.substring(6); // Remove 'data: ' prefix
                  console.log('📨 SSE message:', jsonData);
                  const data: IngestionProgress = JSON.parse(jsonData);
                  
                  // Update ingestion status for table display
                  setIngestionStatus({
                    isIngesting: data.type !== 'completed' && data.type !== 'error',
                    message: data.message,
                    progress: data,
                  });

                  if (data.type === 'completed') {
                    console.log('✅ Ingestion completed:', data);
                    closeSSEConnection();
                    
                    // Show success message with stats
                    if (data.data) {
                      toast.success(
                        `Ingestion completed! ${data.data.total_chunks || 0} chunks, ${data.data.total_images || 0} images processed.`
                      );
                    }
                    
                    // Update status - file list will show updated 'ingested' field on next natural refresh
                    setIngestionStatus({
                      isIngesting: false,
                      message: 'Completed',
                      progress: data,
                    });
                    
                    // Refresh to get updated 'ingested' status from backend
                    fetchFiles();
                    return; // Exit the loop
                  } else if (data.type === 'error') {
                    console.error('❌ Ingestion error:', data);
                    closeSSEConnection();
                    toast.error(data.message || 'Ingestion failed');
                    
                    setIngestionStatus({
                      isIngesting: false,
                      message: 'Failed',
                      progress: data,
                    });
                    return; // Exit the loop
                  }
                } catch (err) {
                  console.error('❌ Error parsing SSE message:', err, line);
                }
              }
            }
          }
        } catch (error: any) {
          if (error.name === 'AbortError') {
            console.log('🔌 SSE connection aborted');
          } else {
            console.error('❌ SSE stream error:', error);
            setIngestionStatus({
              isIngesting: false,
              message: 'Connection lost',
            });
            toast.error('Lost connection to ingestion stream');
          }
        }
      };

      readStream();
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('❌ Error opening SSE connection:', error);
        toast.error('Failed to connect to ingestion stream');
        setIngestionStatus({
          isIngesting: false,
          message: 'Connection failed',
        });
      }
    }
  };

  // Close SSE connection
  const closeSSEConnection = () => {
    if (abortControllerRef.current) {
      console.log('🔌 Closing SSE connection');
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 4 }}>
      <Container maxWidth="xl" sx={{ pt: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', mb: 2 }}>
              RAG Files
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b' }}>
              Manage documents and files for Retrieval-Augmented Generation
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={isDeleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <DeleteIcon />}
              onClick={handleBulkDeleteClick}
              disabled={selectedFiles.length === 0 || isDeleting || isIngesting}
              sx={{
                bgcolor: '#dc2626',
                '&:hover': { bgcolor: '#b91c1c' },
                '&:disabled': { bgcolor: '#cbd5e1' }
              }}
            >
              {isDeleting ? 'Deleting...' : `Delete${selectedFiles.length > 0 ? ` (${selectedFiles.length})` : ''}`}
            </Button>
            {/* <Button
              variant="contained"
              startIcon={isIngesting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <CloudUploadIcon />}
              onClick={handleIngestClick}
              disabled={selectedFiles.length === 0 || isIngesting || isDeleting}
              sx={{
                bgcolor: '#16a34a',
                '&:hover': { bgcolor: '#15803d' },
                '&:disabled': { bgcolor: '#cbd5e1' }
              }}
            >
              {isIngesting ? 'Ingesting...' : `Ingest${selectedFiles.length > 0 ? ` (${selectedFiles.length})` : ''}`}
            </Button> */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(ROUTE_PATHS.RAG_ADD)}
              sx={{
                bgcolor: '#2563eb',
                '&:hover': { bgcolor: '#1d4ed8' }
              }}
            >
              Add File
            </Button>
          </Box>
        </Box>

        {/* Files Table */}
        <Paper sx={{ borderRadius: 3, boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : files.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <InsertDriveFileIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
                No files uploaded yet
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
                Upload your first document to get started
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate(ROUTE_PATHS.RAG_ADD)}
                sx={{
                  bgcolor: '#2563eb',
                  '&:hover': { bgcolor: '#1d4ed8' }
                }}
              >
                Add File
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedFiles.length > 0 && selectedFiles.length < files.length}
                        checked={files.length > 0 && selectedFiles.length === files.length}
                        onChange={handleSelectAll}
                        sx={{
                          color: '#64748b',
                          '&.Mui-checked': { color: '#2563eb' },
                          '&.MuiCheckbox-indeterminate': { color: '#2563eb' }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>File Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Size</TableCell>
                    {/* <TableCell sx={{ fontWeight: 600, color: '#475569' }} align="center">Ingested</TableCell> */}
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }} align="center">Ingestion Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Created At</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Modified At</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', width: 80 }} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {files.map((file) => (
                    <TableRow
                      key={file.path}
                      sx={{
                        '&:hover': { bgcolor: '#f8fafc' },
                        '&:last-child td': { border: 0 },
                        bgcolor: selectedFiles.includes(file.filename) ? '#eff6ff' : 'transparent'
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedFiles.includes(file.filename)}
                          onChange={() => handleSelectFile(file.filename)}
                          sx={{
                            color: '#64748b',
                            '&.Mui-checked': { color: '#2563eb' }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <InsertDriveFileIcon sx={{ color: '#64748b', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {file.filename}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          {formatFileSize(file.size)}
                        </Typography>
                      </TableCell>
                      {/* <TableCell align="center">
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 2,
                            bgcolor: file.ingested ? '#dcfce7' : '#f1f5f9',
                            border: `1px solid ${file.ingested ? '#86efac' : '#cbd5e1'}`
                          }}
                        >
                          {file.ingested ? (
                            <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                          ) : (
                            <CancelIcon sx={{ fontSize: 16, color: '#64748b' }} />
                          )}
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: file.ingested ? '#16a34a' : '#64748b'
                            }}
                          >
                            {file.ingested ? 'Yes' : 'No'}
                          </Typography>
                        </Box>
                      </TableCell> */}
                      <TableCell align="center">
                        {ingestionStatus.isIngesting ? (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 1,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 2,
                              bgcolor: '#eff6ff',
                              border: '1px solid #bfdbfe'
                            }}
                          >
                            <CircularProgress size={14} sx={{ color: '#2563eb' }} />
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                color: '#2563eb'
                              }}
                            >
                              {ingestionStatus.message}
                            </Typography>
                          </Box>
                        ) : ingestionStatus.progress?.type === 'completed' ? (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 2,
                              bgcolor: '#dcfce7',
                              border: '1px solid #86efac'
                            }}
                          >
                            <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                color: '#16a34a'
                              }}
                            >
                              Completed
                            </Typography>
                          </Box>
                        ) : ingestionStatus.progress?.type === 'error' ? (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 2,
                              bgcolor: '#fee2e2',
                              border: '1px solid #fecaca'
                            }}
                          >
                            <ErrorIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                color: '#dc2626'
                              }}
                            >
                              Failed
                            </Typography>
                          </Box>
                        ) : file.ingested ? (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 2,
                              bgcolor: '#dcfce7',
                              border: '1px solid #86efac'
                            }}
                          >
                            <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                color: '#16a34a'
                              }}
                            >
                              Ingested
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          {formatDate(file.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          {formatDate(file.modified_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, file)}
                          sx={{ color: '#64748b' }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleDownload}>
          <DownloadIcon sx={{ mr: 1, fontSize: 18 }} /> Download
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: '#dc2626' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 18 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>
          Delete File
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{selectedFile?.filename}"? This action cannot be undone.
          </DialogContentText>
          {selectedFile?.ingested && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 2
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#991b1b',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                ⚠️ Warning: Ingested File
              </Typography>
              <Typography variant="body2" sx={{ color: '#dc2626', mt: 1 }}>
                This file has been ingested into the RAG knowledge base. Deleting it will remove all associated data from the RAG system.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleDeleteCancel}
            disabled={isDeleting}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: '#64748b'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
            sx={{
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={handleBulkDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>
          Delete Multiple Files
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedFiles.length} file(s)? This action cannot be undone.
          </DialogContentText>
          {selectedFiles.length > 0 && (() => {
            const ingestedFiles = files.filter(f => selectedFiles.includes(f.filename) && f.ingested);
            const hasIngestedFiles = ingestedFiles.length > 0;
            
            return (
              <>
                {hasIngestedFiles && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: 2
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#991b1b',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      ⚠️ Warning: Ingested Files Detected
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#dc2626', mt: 1 }}>
                      {ingestedFiles.length} of the selected files {ingestedFiles.length === 1 ? 'has' : 'have'} been ingested into the RAG knowledge base. Deleting {ingestedFiles.length === 1 ? 'it' : 'them'} will remove all associated data from the RAG system.
                    </Typography>
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" sx={{ color: '#991b1b', fontWeight: 600 }}>
                        Ingested files:
                      </Typography>
                      <Box sx={{ mt: 0.5, maxHeight: '80px', overflowY: 'auto' }}>
                        {ingestedFiles.map((file, index) => (
                          <Typography key={index} variant="body2" sx={{ color: '#dc2626', py: 0.25, fontSize: '0.8rem' }}>
                            • {file.filename}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                    Files to be deleted:
                  </Typography>
                  <Box sx={{ mt: 1, maxHeight: '150px', overflowY: 'auto' }}>
                    {selectedFiles.map((filename, index) => {
                      const file = files.find(f => f.filename === filename);
                      return (
                        <Typography 
                          key={index} 
                          variant="body2" 
                          sx={{ 
                            color: file?.ingested ? '#dc2626' : '#475569', 
                            py: 0.5,
                            fontWeight: file?.ingested ? 600 : 400
                          }}
                        >
                          • {filename} {file?.ingested ? '(Ingested)' : ''}
                        </Typography>
                      );
                    })}
                  </Box>
                </Box>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleBulkDeleteCancel}
            disabled={isDeleting}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: '#64748b'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleBulkDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
            sx={{
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {isDeleting ? 'Deleting...' : `Delete ${selectedFiles.length} File(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ingest Confirmation Dialog */}
      <Dialog
        open={ingestDialogOpen}
        onClose={handleIngestCancel}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: '400px'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>
          Ingest Files
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to ingest {selectedFiles.length} file(s) into the RAG knowledge base?
          </DialogContentText>
          {selectedFiles.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                Files to be ingested:
              </Typography>
              <Box sx={{ mt: 1, maxHeight: '150px', overflowY: 'auto' }}>
                {selectedFiles.map((filename, index) => (
                  <Typography key={index} variant="body2" sx={{ color: '#475569', py: 0.5 }}>
                    • {filename}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleIngestCancel}
            disabled={isIngesting}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              color: '#64748b'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleIngestConfirm}
            variant="contained"
            disabled={isIngesting}
            startIcon={isIngesting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#16a34a',
              '&:hover': { bgcolor: '#15803d' }
            }}
          >
            {isIngesting ? 'Ingesting...' : `Ingest ${selectedFiles.length} File(s)`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
